import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { PATIENT_INFORMATION_STATS } from '../dashboardStatsConfig';

const parsePatientStatDisplay = (rawValue) => {
    const str = String(rawValue ?? '').trim();
    if (!str || str === '—') {
        return { countLabel: '—', shareLabel: '—' };
    }

    const match = str.match(/^([\d,]+)\s*\((\d+(?:\.\d+)?)%\)$/);
    if (match) {
        return {
            countLabel: match[1],
            shareLabel: `${match[2]}%`,
        };
    }

    return { countLabel: str, shareLabel: '—' };
};

function DashboardPatientInformation({ getStat, isLoading, loadError, lastUpdated }) {
    const patientRows = useMemo(
        () =>
            PATIENT_INFORMATION_STATS.filter((stat) => stat.key !== 'TOTAL_PATIENTS').map((stat) => ({
                ...stat,
                ...parsePatientStatDisplay(getStat(stat.key)),
            })),
        [getStat]
    );

    const totalPatients = getStat('TOTAL_PATIENTS');

    return (
        <section className="ecx-page-card ecx-page-panel dashboard-stats-panel dashboard-panel-theme dashboard-panel-theme--indigo">
            <div className="ecx-page-panel__header">
                <div className="d-flex align-items-center justify-content-between gap-3 flex-wrap">
                    <div className="dashboard-stats-panel__heading">
                        <span className="ecx-page-icon-badge">
                            <i className="bi bi-people-fill" />
                        </span>
                        <div>
                            <h2 className="dashboard-stats-panel__title">Patient Information</h2>
                            <p className="dashboard-stats-panel__subtitle mb-0">
                                {lastUpdated
                                    ? `Coverage and opt-in metrics · Updated ${lastUpdated}`
                                    : 'Coverage and communication opt-in metrics'}
                            </p>
                        </div>
                    </div>
                    <Link to="/patient-files" className="btn btn-sm btn-light-primary rounded-pill">
                        Open patient files
                        <i className="bi bi-arrow-right ms-1" />
                    </Link>
                </div>
            </div>

            <div className="ecx-page-panel__body">
                {loadError && (
                    <div className="dashboard-alert" role="alert">
                        <i className="bi bi-exclamation-circle me-2" />
                        {loadError}
                    </div>
                )}

                {isLoading ? (
                    <div className="dashboard-roi" aria-hidden="true">
                        <div className="dashboard-roi__summary dashboard-stat-tile--skeleton" style={{ minHeight: '4.5rem' }} />
                        <div className="dashboard-roi__table">
                            {Array.from({ length: 7 }).map((_, index) => (
                                <div
                                    key={`patient-row-skel-${index}`}
                                    className="dashboard-roi__row dashboard-stat-tile--skeleton"
                                    style={{ minHeight: '3rem', border: 0 }}
                                />
                            ))}
                        </div>
                    </div>
                ) : (
                    <div className="dashboard-roi">
                        <div className="dashboard-roi__summary">
                            <div className="dashboard-roi__summary-copy">
                                <span className="dashboard-roi__summary-label">Total patients</span>
                                <span className="dashboard-roi__summary-hint">
                                    Active coverage and communication opt-in across your patient list
                                </span>
                            </div>
                            <span className="dashboard-roi__summary-value">{totalPatients}</span>
                        </div>

                        <div className="dashboard-roi__table" role="table" aria-label="Patient information breakdown">
                            <div className="dashboard-roi__head" role="row">
                                <span role="columnheader">Metric</span>
                                <span role="columnheader">Share</span>
                                <span role="columnheader">Patients</span>
                            </div>

                            {patientRows.map((stat) => (
                                <div key={stat.key} className="dashboard-roi__row" role="row">
                                    <div className="dashboard-roi__metric" role="cell">
                                        <span className="dashboard-roi__metric-icon" aria-hidden="true">
                                            <i className={`bi ${stat.icon}`} />
                                        </span>
                                        <span className="dashboard-roi__metric-label">{stat.label}</span>
                                    </div>
                                    <span className="dashboard-roi__calc" role="cell">
                                        {stat.shareLabel}
                                    </span>
                                    <span className="dashboard-roi__amount" role="cell">
                                        {stat.countLabel}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </section>
    );
}

export default DashboardPatientInformation;
